import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import buttonStyles from '../components/Buttons.module.css';
import pawImg from '../assets/paw.png';
import EyeIcon from '../assets/icons/eye.svg';
import EyeSlashIcon from '../assets/icons/eye-slash.svg';
import { useAuth } from '../auth/AuthContext';
import FlashMessage from '../components/FlashMessage';

function useRedirectTo(): string {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirectTo');
  return redirectTo || '/';
}

function useMessage(): string | null {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get('message');
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup, isLoading } = useAuth();
  const redirectTo = useRedirectTo();
  const message = useMessage();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [flashMessage, setFlashMessage] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [touched, setTouched] = useState({ email: false, password: false, passwordConfirmation: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const isEmailValid = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }, [email]);

  const isPasswordValid = useMemo(() => {
    return password.length >= 8;
  }, [password]);

  const isPasswordMatch = useMemo(() => {
    return password === passwordConfirmation && passwordConfirmation.length >= 8;
  }, [password, passwordConfirmation]);

  const isValid = useMemo(() => {
    if (fullName.trim().length === 0) return false;
    if (!isEmailValid) return false;
    if (!isPasswordValid) return false;
    if (!isPasswordMatch) return false;
    if (!agreedToTerms) return false;
    return true;
  }, [fullName, isEmailValid, isPasswordValid, isPasswordMatch, agreedToTerms]);

  const handleResendConfirmation = async () => {
    if (resendLoading) return;
    
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const authApi = await import('../api/auth');
      await authApi.resendConfirmation(email.trim());
      setResendSuccess(true);
      setError('Confirmation email sent! Please check your inbox.');
      setTimeout(() => {
        setResendSuccess(false);
        setShowResend(false);
      }, 5000);
    } catch (err: any) {
      setError('Failed to resend confirmation email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // Split full name into first and last
      const nameParts = fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      
      const result = await signup({
        first_name: firstName,
        last_name: lastName,
        email: email.trim(),
        password,
        password_confirmation: passwordConfirmation,
        accepted_terms: agreedToTerms,
      });
      
      // Show success message and redirect to login
      setFlashMessage({ message: result.message, type: 'success' });
      
      // Redirect to login page after showing the message
      setTimeout(() => {
        navigate('/login?message=Please check your email to confirm your account', { replace: true });
      }, 2000);
    } catch (err: any) {
      const errorCode = err?.response?.data?.error_code || err?.response?.data?.code;
      const status = err?.response?.status;
      const errors = err?.response?.data?.errors;
      
      // Handle specific error cases with friendly messages
      if (errorCode === 'EMAIL_ALREADY_REGISTERED') {
        setError('An account with this email already exists. Please log in or use a different email.');
      } else if (errorCode === 'EMAIL_NOT_CONFIRMED') {
        setError('An account with this email exists but is not confirmed. Please check your email for the confirmation link.');
        setShowResend(true);
      } else if (Array.isArray(errors) && errors.length > 0) {
        // Handle validation errors array from backend
        setError(errors.join('. '));
      } else if (status === 429) {
        setError('Too many signup attempts. Please try again later.');
      } else if (status >= 500) {
        setError('Our servers are experiencing issues. Please try again in a few moments.');
      } else if (!navigator.onLine) {
        setError('No internet connection. Please check your network and try again.');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        setError('Request timed out. Please try again.');
      } else {
        setError('Unable to create account. Please check your information and try again.');
      }
    }
  };

  return (
    <div className="page-container cream-bg">
      {flashMessage && (
        <FlashMessage
          message={flashMessage.message}
          type={flashMessage.type}
          onClose={() => setFlashMessage(null)}
        />
      )}
      <div className="hero-paw-bg">
        <img src={pawImg} className="hero-paw-float paw1" alt="" />
        <img src={pawImg} className="hero-paw-float paw2" alt="" />
        <img src={pawImg} className="hero-paw-float paw3" alt="" />
        <img src={pawImg} className="hero-paw-float paw4" alt="" />
        <img src={pawImg} className="hero-paw-float paw5" alt="" />
        <img src={pawImg} className="hero-paw-float paw6" alt="" />
        <img src={pawImg} className="hero-paw-float paw7" alt="" />
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '2rem' }}>
        <div className="page-intro" style={{ padding: 0 }}>
          <h1 className="page-title">
            {message ? "Create your account to unlock this recipe" : "Create account"}
          </h1>
          <div className="page-intro-underline" />
          {message && (
            <p style={{ marginTop: '1rem', color: '#b24646', fontWeight: 500, fontSize: '1rem', textAlign: 'center' }}>
              {message}
            </p>
          )}
        </div>

        <form onSubmit={onSubmit} style={{ background: '#fff', borderRadius: 16, padding: '1.25rem', border: '1px solid rgba(178, 70, 70, 0.18)' }}>
          {error && (
            <div style={{ marginBottom: 12, color: resendSuccess ? '#16a34a' : '#b24646', fontWeight: 600 }}>
              {error}
            </div>
          )}
          
          {showResend && !resendSuccess && (
            <div style={{ 
              marginBottom: 12, 
              padding: '12px', 
              background: '#fff7ed', 
              borderRadius: 8, 
              border: '1px solid #fed7aa' 
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#7c2d12' }}>
                Didn't receive the confirmation email?
              </p>
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={resendLoading}
                className={`${buttonStyles.antBtn} ${buttonStyles.antBtnSecondary}`}
                style={{ fontSize: '0.9rem', padding: '6px 12px' }}
              >
                {resendLoading ? 'Sending...' : 'Resend confirmation email'}
              </button>
            </div>
          )}

          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#333' }}>Full Name <span style={{ color: '#d9534f' }}>*</span></label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            type="text"
            autoComplete="name"
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.15)', marginBottom: 14 }}
          />

          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#333' }}>Email <span style={{ color: '#d9534f' }}>*</span></label>
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched({ ...touched, email: true })}
            type="email"
            autoComplete="email"
            style={{ 
              width: '100%', 
              padding: '12px 14px', 
              borderRadius: 10, 
              border: touched.email && !isEmailValid ? '2px solid #d9534f' : '1px solid rgba(0,0,0,0.15)', 
              marginBottom: touched.email && !isEmailValid ? 4 : 14
            }}
          />
          {touched.email && !isEmailValid && (
            <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', color: '#d9534f' }}>
              Please enter a valid email address
            </p>
          )}

          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#333' }}>Password <span style={{ color: '#d9534f' }}>*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => setTouched({ ...touched, password: true })}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              style={{ 
                width: '100%', 
                padding: '12px 14px', 
                paddingRight: '45px',
                borderRadius: 10, 
                border: touched.password && !isPasswordValid ? '2px solid #d9534f' : '1px solid rgba(0,0,0,0.15)', 
                marginBottom: 4 
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img 
                src={showPassword ? EyeIcon : EyeSlashIcon} 
                alt={showPassword ? 'Hide password' : 'Show password'}
                style={{ width: '20px', height: '20px' }}
              />
            </button>
          </div>
          <p style={{ margin: '0 0 14px 0', fontSize: '0.85rem', color: touched.password && !isPasswordValid ? '#d9534f' : '#666' }}>
            Use at least 8 characters
          </p>

          <label style={{ display: 'block', fontWeight: 700, marginBottom: 6, color: '#333' }}>Confirm password <span style={{ color: '#d9534f' }}>*</span></label>
          <div style={{ position: 'relative' }}>
            <input
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              onBlur={() => setTouched({ ...touched, passwordConfirmation: true })}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              style={{ 
                width: '100%', 
                padding: '12px 14px', 
                paddingRight: '45px',
                borderRadius: 10, 
                border: touched.passwordConfirmation && !isPasswordMatch ? '2px solid #d9534f' : '1px solid rgba(0,0,0,0.15)', 
                marginBottom: 16 
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '5px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img 
                src={showConfirmPassword ? EyeIcon : EyeSlashIcon} 
                alt={showConfirmPassword ? 'Hide password' : 'Show password'}
                style={{ width: '20px', height: '20px' }}
              />
            </button>
          </div>

          <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
            <input
              type="checkbox"
              id="terms-checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              style={{ marginTop: '0.25rem', cursor: 'pointer', flexShrink: 0 }}
            />
            <label htmlFor="terms-checkbox" style={{ fontSize: '0.9rem', color: '#555', lineHeight: '1.5', cursor: 'pointer' }}>
              I agree to the <Link to="/terms" target="_blank" style={{ color: '#C44735', textDecoration: 'none', fontWeight: 600 }}>Terms of Service</Link> and <Link to="/privacy" target="_blank" style={{ color: '#C44735', textDecoration: 'none', fontWeight: 600 }}>Privacy Policy</Link>, and acknowledge the disclaimer regarding veterinary advice.
            </label>
          </div>

          <button
            className={`${buttonStyles.antBtn} ${buttonStyles.antBtnPrimary}`}
            type="submit"
            disabled={!isValid || isLoading}
            style={{ width: '100%' }}
          >
            {isLoading ? 'Creating…' : (message ? 'Create account & continue' : 'Create account')}
          </button>

          {message && (
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666', textAlign: 'center' }}>
              You'll review payment details on the next step.
            </p>
          )}

          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#555' }}>Already have an account?</span>
            <Link
              to={`/login?redirectTo=${encodeURIComponent(redirectTo)}`}
              className={buttonStyles.textLinkBtn}
            >
              Log in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
